import './styles.css';
import { SpatialRenderer } from './renderer.js';
import { FallbackController, HeadTracker } from './tracking.js';

const $ = (selector) => document.querySelector(selector);
const dom = {
  app: $('#app'),
  canvas: $('#scene'),
  video: $('#webcam'),
  status: $('#status'),
  statusText: $('#status-text'),
  intro: $('#intro'),
  startCamera: $('#start-camera'),
  cameraButton: $('#camera-button'),
  calibrateButton: $('#calibrate-button'),
  fullscreenButton: $('#fullscreen-button'),
  helpButton: $('#help-button'),
  helpPanel: $('#help-panel'),
  closeHelp: $('#close-help'),
  loading: $('#loading'),
  loadingProgress: $('#loading-progress'),
  loadingLabel: $('#loading-label'),
  dropZone: $('#drop-zone'),
  sourceLabel: $('#source-label'),
};

const qa = {
  ready: false,
  tracking: false,
  source: 'demo',
  eye: { x: 0, y: 0, z: 3.2 },
  fps: 0,
  assetLoaded: false,
};
window.__SPATIAL_WINDOW__ = qa;

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
let targetEye = { x: 0, y: 0, z: 3.2 };
let currentEye = { ...targetEye };
let lastFrame = performance.now();
let lastDirectInput = performance.now();
let dragDepth = 0;

function setStatus(state, label) {
  dom.status.dataset.state = state;
  dom.statusText.textContent = label;
}

function setSource(source) {
  qa.source = source;
  dom.sourceLabel.textContent = source === 'webcam' ? 'HEAD TRACKING' : source.toUpperCase();
}

function setLoading(ratio, label) {
  dom.loading.classList.remove('is-hidden');
  dom.loadingProgress.style.width = `${Math.max(8, Math.round(ratio * 100))}%`;
  dom.loadingLabel.textContent = label;
  if (ratio >= 1) {
    window.setTimeout(() => dom.loading.classList.add('is-hidden'), 500);
  }
}

let renderer;
try {
  renderer = new SpatialRenderer(dom.canvas, setLoading);
} catch (error) {
  console.error(error);
  setStatus('error', 'WebGL nicht verfügbar');
  setLoading(0, '3D-Darstellung wird nicht unterstützt');
  throw error;
}

const headTracker = new HeadTracker(dom.video, {
  onEye(eye) {
    targetEye = eye;
    lastDirectInput = performance.now();
    qa.tracking = true;
    setSource('webcam');
    dom.calibrateButton.disabled = false;
  },
  onState(state, label) {
    if (state !== 'tracking') qa.tracking = false;
    if (state === 'requesting' || state === 'loading' || state === 'active') setStatus('ready', label);
    if (state === 'tracking') setStatus('tracking', label);
    if (state === 'stopped') {
      setStatus(qa.ready ? 'ready' : 'loading', qa.ready ? 'Bereit' : 'Lädt');
      dom.calibrateButton.disabled = true;
      dom.cameraButton.setAttribute('aria-pressed', 'false');
    }
  },
});

const fallback = new FallbackController(dom.app, (eye, source) => {
  if (headTracker.faceVisible) return;
  targetEye = eye;
  lastDirectInput = performance.now();
  setSource(source);
  dom.intro.classList.add('is-hidden');
});

async function toggleCamera() {
  if (headTracker.active) {
    headTracker.stop();
    qa.tracking = false;
    setSource('demo');
    return;
  }

  dom.cameraButton.disabled = true;
  dom.startCamera.disabled = true;
  dom.intro.classList.add('is-hidden');
  try {
    await headTracker.start();
    dom.cameraButton.setAttribute('aria-pressed', 'true');
  } catch (error) {
    console.warn('Camera could not start.', error);
    const denied = error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError';
    setStatus('error', denied ? 'Kamera nicht erlaubt' : 'Kamera-Fehler');
    setSource('demo');
    window.setTimeout(() => {
      if (!headTracker.active) setStatus(qa.ready ? 'ready' : 'loading', qa.ready ? 'Bereit' : 'Lädt');
    }, 2800);
  } finally {
    dom.cameraButton.disabled = false;
    dom.startCamera.disabled = false;
  }
}

dom.startCamera.addEventListener('click', toggleCamera);
dom.cameraButton.addEventListener('click', toggleCamera);
dom.calibrateButton.addEventListener('click', () => {
  if (headTracker.calibrate()) {
    setStatus('tracking', 'Kalibriert');
    window.setTimeout(() => {
      if (headTracker.faceVisible) setStatus('tracking', 'Gesicht erkannt');
    }, 1100);
  }
});

dom.fullscreenButton.addEventListener('click', async () => {
  try {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await dom.app.requestFullscreen();
  } catch (error) {
    console.warn('Fullscreen unavailable.', error);
  }
});

document.addEventListener('fullscreenchange', () => {
  dom.fullscreenButton.setAttribute('aria-pressed', String(Boolean(document.fullscreenElement)));
});

function setHelp(open) {
  dom.helpPanel.hidden = !open;
  dom.helpButton.setAttribute('aria-expanded', String(open));
}
dom.helpButton.addEventListener('click', () => setHelp(dom.helpPanel.hidden));
dom.closeHelp.addEventListener('click', () => setHelp(false));
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && !dom.helpPanel.hidden) setHelp(false);
});

function showDropZone(show) {
  dom.dropZone.classList.toggle('is-visible', show);
  dom.dropZone.setAttribute('aria-hidden', String(!show));
}

dom.app.addEventListener('dragenter', (event) => {
  event.preventDefault();
  dragDepth += 1;
  showDropZone(true);
});
dom.app.addEventListener('dragover', (event) => event.preventDefault());
dom.app.addEventListener('dragleave', (event) => {
  event.preventDefault();
  dragDepth = Math.max(0, dragDepth - 1);
  if (!dragDepth) showDropZone(false);
});
dom.app.addEventListener('drop', async (event) => {
  event.preventDefault();
  dragDepth = 0;
  showDropZone(false);
  const file = event.dataTransfer?.files?.[0];
  if (!file) return;
  try {
    setStatus('loading', 'Datei wird geladen');
    await renderer.loadFile(file);
    qa.assetLoaded = true;
    setStatus('ready', 'Eigene Szene bereit');
    dom.intro.classList.add('is-hidden');
  } catch (error) {
    console.error(error);
    setStatus('error', error.message || 'Datei nicht lesbar');
    setLoading(0, error.message || 'Datei nicht lesbar');
  }
});

function animate(now) {
  const dt = Math.min((now - lastFrame) / 1000, 0.1);
  lastFrame = now;

  const inactiveFor = now - lastDirectInput;
  if (!headTracker.faceVisible && inactiveFor > 2600) {
    if (prefersReducedMotion.matches) {
      targetEye = { x: 0, y: 0, z: 3.2 };
    } else {
      const t = now * 0.00028;
      targetEye = {
        x: Math.sin(t) * 0.38,
        y: Math.sin(t * 0.71 + 1.1) * 0.17,
        z: 3.2 + Math.sin(t * 0.43) * 0.12,
      };
    }
    setSource('demo');
  }

  const smoothing = 1 - Math.exp(-dt * (prefersReducedMotion.matches ? 18 : 9.5));
  currentEye.x += (targetEye.x - currentEye.x) * smoothing;
  currentEye.y += (targetEye.y - currentEye.y) * smoothing;
  currentEye.z += (targetEye.z - currentEye.z) * smoothing;
  renderer.setEye(currentEye);

  qa.eye.x = Number(currentEye.x.toFixed(4));
  qa.eye.y = Number(currentEye.y.toFixed(4));
  qa.eye.z = Number(currentEye.z.toFixed(4));
  qa.fps = renderer.fps;
  qa.assetLoaded = renderer.assetLoaded;

  requestAnimationFrame(animate);
}
requestAnimationFrame(animate);

async function boot() {
  setStatus('loading', 'Lädt');
  try {
    await renderer.loadDefault();
    qa.ready = true;
    qa.assetLoaded = true;
    setStatus('ready', 'Bereit');
    window.setTimeout(() => dom.intro.classList.add('is-hidden'), 6500);
  } catch (error) {
    console.error(error);
    setStatus('error', 'Szene nicht geladen');
    setLoading(0, '3D-Szene konnte nicht geladen werden');
  }
}

boot();

window.addEventListener('beforeunload', () => {
  headTracker.stop();
  fallback.destroy();
  renderer.destroy();
}, { once: true });
